'use client';

import { useCallback } from 'react';

import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { useQueryClient } from '@tanstack/react-query';

import { analytics } from '@ci/analytics';
import { useMonitoring } from '@ci/monitoring/hooks';
import { useAppEvents } from '@ci/shared/events';
import {
  type ForcedSignOutPayload,
  useAuthChangeListener,
} from '@ci/supabase/hooks/use-auth-change-listener';
import {
  USER_QUERY_KEY,
  resolveAuthCacheAction,
} from '@ci/supabase/hooks/use-user';
import type { JWTUserData } from '@ci/supabase/types';
import { resetAccountAnalyticsDedupe } from '@ci/team-accounts/lib/account-analytics';

import pathsConfig from '~/config/paths.config';
import { teardownIntercomIdentity } from '~/lib/intercom/intercom-identity';

export function AuthProvider(props: React.PropsWithChildren) {
  const dispatchEvent = useDispatchAppEventFromAuthEvent();
  const onForcedSignOut = useForcedSignOutMonitoring();
  const syncQueryCacheWithAuth = useSyncQueryCacheWithAuth();

  const onEvent = useCallback(
    (event: AuthChangeEvent, session: Session | null) => {
      // Keep the React Query cache in sync with the auth state BEFORE anything
      // navigates, so a re-login never shows the previous account's data.
      syncQueryCacheWithAuth(event, session);

      dispatchEvent(event, session?.user.id, {
        email: session?.user.email ?? '',
        // Signup date for analytics cohorting. Only available here: the
        // workspace context carries JWTUserData, which has no created_at.
        createdAt: session?.user.created_at ?? '',
      });
    },
    [dispatchEvent, syncQueryCacheWithAuth],
  );

  useAuthChangeListener({
    signInPath: pathsConfig.auth.signIn,
    onEvent,
    onForcedSignOut,
  });

  return props.children;
}

/**
 * @name useSyncQueryCacheWithAuth
 * @description Invalidates/clears the React Query cache on auth transitions.
 * `useUser` (and other queries) cache under static keys with refetch disabled,
 * so without this a re-login keeps showing the previous account until a hard
 * refresh — e.g. deleting an account and logging in as another still showed
 * the deleted email.
 */
function useSyncQueryCacheWithAuth() {
  const queryClient = useQueryClient();

  return useCallback(
    (event: AuthChangeEvent, session: Session | null) => {
      const cachedUser = queryClient.getQueryData<JWTUserData>(USER_QUERY_KEY);
      const action = resolveAuthCacheAction(
        event,
        session?.user.id,
        cachedUser?.id,
      );

      if (action === 'clear') {
        // Drop everything cached for the previous session/account so it can't
        // leak into the next one.
        queryClient.clear();
      } else if (action === 'refresh-user') {
        // Same account — just refresh the displayed identity.
        void queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
      }
    },
    [queryClient],
  );
}

/**
 * @name useForcedSignOutMonitoring
 * @description Reports forced sign-outs on a private route (token-refresh race,
 * idle expiry, other-tab sign-out — PAY-1006) to the monitoring service as a
 * grouped exception, so we finally have error context for these (the ticket
 * flagged that there was none) and can measure how often it fires in
 * production.
 */
function useForcedSignOutMonitoring() {
  const monitoring = useMonitoring();

  return useCallback(
    (payload: ForcedSignOutPayload) => {
      void monitoring.captureException(
        new Error('Forced sign-out during active session'),
        {
          name: 'auth.forced_sign_out',
          event: payload.event,
          pathname: payload.pathname,
        },
      );
    },
    [monitoring],
  );
}

function useDispatchAppEventFromAuthEvent() {
  const { emit } = useAppEvents();
  const monitoring = useMonitoring();

  return useCallback(
    (
      type: AuthChangeEvent,
      userId: string | undefined,
      traits: Record<string, string> = {},
    ) => {
      switch (type) {
        case 'INITIAL_SESSION':
          if (userId) {
            emit({
              type: 'user.signedIn',
              payload: { userId, ...traits },
            });

            monitoring.identifyUser({ id: userId, ...traits });
          }

          break;

        case 'SIGNED_IN':
          if (userId) {
            emit({
              type: 'user.signedIn',
              payload: { userId, ...traits },
            });

            monitoring.identifyUser({ id: userId, ...traits });
          }

          break;

        case 'SIGNED_OUT':
          // PAY-3983: forget the identity everywhere it is kept in the
          // browser, so the next person to sign in here does not inherit it.
          // Segment's reset() clears only Segment; Intercom keeps its own
          // session (and the previous user's conversations) until it is shut
          // down. Runs on every SIGNED_OUT, the impersonation hand-over
          // included, which ends the admin's own Messenger session.
          void analytics.reset();
          teardownIntercomIdentity();
          resetAccountAnalyticsDedupe();

          break;

        case 'USER_UPDATED':
          // Deliberately does NOT spread `traits`. Those are identify traits
          // (email, signup date) and the identify calls above already carry
          // them; spreading them here put email on a tracked EVENT, which is
          // exactly what US-4 removed everywhere else.
          emit({
            type: 'user.updated',
            payload: { userId: userId! },
          });

          break;
      }
    },
    [emit, monitoring],
  );
}
